(ns graph-ui.web.edn-indent
  "EDN <-> indentation-based ('bracket-stripped') format converter.

  The bracket-stripped format replaces {} [] () with indentation, so deeply
  nested EDN widget trees become readable without paren-matching. Structural
  cues that brackets provided are restored via:

  - Vector IDs: each vector gets a hierarchical ID (e.g. '1.1', '3.1.1')
    shown as a trailing ;; comment on the opening :key line, so opening and
    closing markers can be match-paired at a glance.
  - End markers: multi-item vectors close with ';; ─┘ <key>  <id>' at the
    vector's own indent — the bottom-right corner glyph is the visual twin
    of ']', not a tree branch.
  - Sibling spacers: blank line between '- ' map items, grouping each
    widget's properties into a visual block.
  - Header enrichment: for widget maps, :title or :value is pulled onto the
    '- :widget :kind' line so the folded view is self-describing.

  :widget :box
  :title \"map of the system\"
  :children  ;; 1.1

    - :widget :text  :value \"Hierarchical architecture view...\"
      :widget-id \"uuid-1\"

    - :widget :tab-set  :active 0
      :children  ;; 3.1

        - :widget :box  :title \"layers\"

  EDN remains the SSOT — this is an authoring surface. The converter is
  pure (no I/O, no state), so it is unit-tested headlessly.

  Format rules:
  - Map entries: ':key value' (scalar on same line) or ':key' alone (nested
    map/vector on following indented lines). Vector values get a trailing
    ';; <id>' comment with their hierarchical vector ID.
  - Vector items: '- ' prefix. A '- :key value' line starts a MAP item;
    '- :key value :key2 value2' starts a MAP item with multiple inline
    entries (header enrichment); '- scalar' starts a SCALAR item.
  - End markers: multi-item vectors close with ';; ─┘ <key>  <id>' at the
    vector's indent level. Single-item vectors omit the marker.
  - Sibling spacers: blank line between '- ' map items.
  - Indentation: 2 spaces per level.
  - Scalars: keywords (:foo), strings (\"...\"), numbers, booleans, nil,
    symbols ($session-id), UUIDs.
  - Comments: ';; ...' lines are skipped by the parser. Trailing ';; ...'
    comments on data lines are stripped before parsing.

  Locate support: indent-node-span finds the byte range of the widget node
  containing ':widget-id \"<id>\"' in the indent text — the bracket-stripped
  counterpart of locate/node-span for EDN text."
  (:require [clojure.string :as str]
            [cljs.reader :as reader]))

;; ── EDN -> indent ─────────────────────────────────────────────────────

(declare write-map-entries)
(declare write-value)

(defn- indent-spaces [n]
  (str/join (repeat n "  ")))

(defn- scalar? [x]
  (or (keyword? x) (string? x) (number? x) (boolean? x) (nil? x) (symbol? x)))

(defn- edn-scalar-str [x]
  (cond
    (nil? x)   "nil"
    (true? x)  "true"
    (false? x) "false"
    (string? x) (pr-str x)
    :else      (str x)))

;; ── Context: widget numbering + vector IDs ───────────────────────────
;;
;; A context atom tracks {:counter N :widget-num W :vec-ord V} as the tree
;; is walked depth-first. Each map with :widget gets the next counter value
;; (pre-order DFS) and resets vec-ord. Each vector inside widget W gets the
;; ID 'W.<ord>' (usually .1 for :children, the only vector in a widget).

(defn- assign-widget-num!
  "If map m has :widget, assign the next DFS widget number in ctx and reset
  vec-ord. Returns ctx unchanged (mutation is via swap!)."
  [m ctx]
  (when (contains? m :widget)
    (let [wn (inc (:counter @ctx))]
      (swap! ctx assoc :widget-num wn :counter wn :vec-ord 0)))
  ctx)

(defn- next-vec-id!
  "Increment vec-ord in ctx and return the vector ID string '<widget-num>.<ord>'."
  [ctx]
  (let [vo (inc (:vec-ord @ctx))
        wn (:widget-num @ctx)]
    (swap! ctx assoc :vec-ord vo)
    (str wn "." vo)))

(defn- write-entry
  "Render a single map entry ':key value' at the given indent. Scalars share
  the key's line. Vectors get a trailing ';; <id>' comment on the opening
  line and a ';; ─┘ <key>  <id>' end marker after multi-item vectors. Nested
  maps go on following indented lines."
  [k v indent ctx]
  (cond
    (scalar? v)
    (str (indent-spaces indent) (edn-scalar-str k) " " (edn-scalar-str v))

    (vector? v)
    (let [vid     (next-vec-id! ctx)
          body    (write-value v (inc indent) ctx)
          marker? (> (count v) 1)
          k-label (if (keyword? k) (name k) (edn-scalar-str k))
          end     (when marker?
                    (str "\n" (indent-spaces indent) ";; ─┘ " k-label "  " vid))]
      (str (indent-spaces indent) (edn-scalar-str k) "  ;; " vid "\n"
           body
           (or end "")))

    :else
    (str (indent-spaces indent) (edn-scalar-str k) "\n"
         (write-value v (inc indent) ctx))))

(defn- write-map
  "Render a map as indented ':key value' lines. Nested maps/vectors go on
  following indented lines; scalars share the key's line. Assigns a widget
  number if the map has :widget; vector values get IDs and end markers."
  [m indent ctx]
  (assign-widget-num! m ctx)
  (let [entries (seq m)]
    (if (empty? entries)
      ""
      (str/join "\n"
        (for [[k v] entries]
          (write-entry k v indent ctx))))))

(defn- write-vector
  "Render a vector as '- ' prefixed items with blank-line spacers between
  map items. Each item is either a scalar or a map (whose entries follow on
  indented lines under the '- ' line)."
  [v indent ctx]
  (if (empty? v)
    ""
    (str/join "\n\n"
      (for [item v]
        (if (scalar? item)
          (str (indent-spaces indent) "- " (edn-scalar-str item))
          (str (indent-spaces indent) "- "
               (write-map-entries item indent 2 ctx)))))))

(defn- descriptive-key
  "Find the key to pull onto the header line for a widget map.
  Priority: :title > :value > :placeholder. Returns nil for non-widget maps."
  [m]
  (when (contains? m :widget)
    (or (and (contains? m :title) :title)
        (and (contains? m :value) :value)
        (and (contains? m :placeholder) :placeholder))))

(defn- write-map-entries
  "Render a map's entries starting on the '- ' line. For widget maps, the
  header is enriched: ':widget :kind  :title \"...\"' (or :value/:placeholder)
  goes on the '- ' line, and the enriched field is omitted from the indented
  entries below. For non-widget maps, the first entry goes inline (legacy
  behavior). `indent` is the VECTOR's indent level; rest entries go at
  (inc indent) to match what parse-map expects when called at (inc indent)."
  [m indent first-col ctx]
  (assign-widget-num! m ctx)
  (let [desc-key (descriptive-key m)
        header   (when (contains? m :widget)
                   (if desc-key
                     (str ":widget " (edn-scalar-str (:widget m))
                          "  " (edn-scalar-str desc-key) " " (edn-scalar-str (get m desc-key)))
                     (str ":widget " (edn-scalar-str (:widget m)))))
        rest-m   (cond
                   (and header desc-key) (dissoc m :widget desc-key)
                   header                (dissoc m :widget)
                   :else                 m)
        entries  (seq rest-m)]
    (cond
      (and (empty? entries) (nil? header)) "{}"
      (empty? entries) header
      header
      (let [ri (inc indent)]
        (str header "\n"
             (str/join "\n" (for [[k v] entries] (write-entry k v ri ctx)))))
      :else
      (let [[[_k0 v0] & rest-entries] entries
            first-line (if (scalar? v0)
                         (str (edn-scalar-str (key (first entries))) " " (edn-scalar-str v0))
                         (str (edn-scalar-str (key (first entries))) "\n"
                              (write-value v0 (inc indent) ctx)))
            rest-lines (when (seq rest-entries)
                         (str/join "\n"
                           (for [[k v] rest-entries]
                             (write-entry k v (inc indent) ctx))))]
        (str first-line (when rest-lines (str "\n" rest-lines)))))))

(defn- write-value
  "Render a value (map, vector, or scalar) at the given indent level."
  [x indent ctx]
  (cond
    (map? x)    (write-map x indent ctx)
    (vector? x) (write-vector x indent ctx)
    :else       (str (indent-spaces indent) (edn-scalar-str x))))

(defn edn->indent
  "Convert EDN text to bracket-stripped (indentation-based) text. Returns
  the indent string, or the original text if the EDN doesn't parse (a
  half-typed buffer is an ordinary editor state, not an exception)."
  [edn-text]
  (if (str/blank? edn-text)
    ""
    (try
      (let [data (reader/read-string edn-text)
            ctx  (atom {:counter 0 :widget-num nil :vec-ord 0})]
        (write-value data 0 ctx))
      (catch :default _e
        edn-text))))

;; ── indent -> EDN ─────────────────────────────────────────────────────

(defn- strip-trailing-comment
  "Strip a trailing '  ;; ...' comment from a line, but only if the ;; is
  not inside a string literal (checked by counting unescaped quotes before
  it — even count means outside a string). This lets vector IDs appear as
  ':children  ;; 1.1' on opening lines without confusing the parser."
  [line]
  (let [idx (str/index-of line "  ;;")]
    (if (nil? idx)
      line
      (let [before       (subs line 0 idx)
            quote-count  (count (filter #(= % \") before))]
        (if (even? quote-count)
          (str/trimr before)
          line)))))

(defn- parse-line
  "Parse a line into [indent-level content]. Returns nil for blank/comment
  lines. Strips trailing ';; ...' comments (vector IDs) before parsing."
  [line]
  (let [trimmed (str/trimr (strip-trailing-comment line))]
    (if (or (str/blank? trimmed)
            (str/starts-with? (str/triml trimmed) ";;"))
      nil
      (let [spaces (count (take-while #(= \space %) trimmed))
            content (subs trimmed spaces)]
        [(/ spaces 2) content]))))

(defn- parse-scalar
  "Parse a scalar token from indent text. Handles keywords, strings, numbers,
  booleans, nil, and symbols."
  [s]
  (let [s (str/trim s)]
    (cond
      (= s "nil")   nil
      (= s "true")  true
      (= s "false") false
      (str/starts-with? s ":") (keyword (subs s 1))
      (str/starts-with? s "\"") (reader/read-string s)
      (re-matches #"-?\d+\.?\d*" s) (reader/read-string s)
      :else (symbol s))))

(defn- split-key-value
  "Split ':key value' into [key-str value-str], or [key-str nil] if the key
  is alone on the line. Returns nil if the line doesn't start with a keyword."
  [content]
  (let [content (str/trim content)
        space-idx (str/index-of content " ")]
    (if space-idx
      [(subs content 0 space-idx) (str/trim (subs content (inc space-idx)))]
      [content nil])))

(defn- vector-item? [content]
  (str/starts-with? content "- "))

(defn- strip-dash [content]
  (str/trim (subs content 2)))

(defn- tokenize-line
  "Tokenize a content string into a sequence of scalar token strings.
  Handles keywords (:foo), strings (\"...\" with escape handling), and
  other non-space tokens (numbers, booleans, symbols). Used to parse
  multiple inline ':key value' pairs on '- ' header lines (header
  enrichment: '- :widget :text  :value \"hello\"')."
  [content]
  (loop [i 0
         n (count content)
         tokens []]
    (if (>= i n)
      (vec tokens)
      (let [c (nth content i)]
        (cond
          ;; Skip whitespace
          (or (= c \space) (= c \tab))
          (recur (inc i) n tokens)

          ;; String: find closing quote (handle \\ escapes)
          (= c \")
          (let [end (loop [j (inc i) esc? false]
                      (cond
                        (>= j n) n
                        (and (= (nth content j) \") (not esc?)) (inc j)
                        (= (nth content j) \\) (recur (inc j) true)
                        :else (recur (inc j) false)))]
            (recur end n (conj tokens (subs content i end))))

          ;; Other token: read until whitespace
          :else
          (let [end (loop [j (inc i)]
                      (cond
                        (>= j n) n
                        (or (= (nth content j) \space) (= (nth content j) \tab)) j
                        :else (recur (inc j))))]
            (recur end n (conj tokens (subs content i end)))))))))

(defn- parse-inline-entries
  "Parse a sequence of tokens (alternating :keyword value) into a map.
  Used for enriched '- ' header lines that carry multiple key-value pairs."
  [tokens]
  (loop [tokens tokens
         entries {}]
    (if (empty? tokens)
      entries
      (let [k-str (first tokens)
            v-str (second tokens)]
        (if (and k-str v-str (str/starts-with? k-str ":"))
          (recur (drop 2 tokens)
                 (assoc entries (parse-scalar k-str) (parse-scalar v-str)))
          entries)))))

(declare parse-indent-block)

(defn- parse-map
  "Parse map entries from lines at indent level `indent`. Returns [map remaining-lines]."
  [lines indent]
  (loop [lines lines
         entries {}]
    (if (empty? lines)
      [entries lines]
      (let [[line-indent content] (first lines)]
        (cond
          ;; Line at lesser indent or a vector item at our level — we're done
          (or (< line-indent indent)
              (and (= line-indent indent) (vector-item? content)))
          [(into {} entries) lines]

          ;; Line at deeper indent — skip (shouldn't happen at this level)
          (> line-indent indent)
          (recur (rest lines) entries)

          ;; A ':key value' or ':key' line at our indent
          :else
          (let [[k-str v-str] (split-key-value content)
                k (parse-scalar k-str)]
            (if v-str
              ;; Scalar value on same line
              (recur (rest lines) (assoc entries k (parse-scalar v-str)))
              ;; Nested value on following lines
              (let [[v rest-lines] (parse-indent-block (rest lines) (inc indent))]
                (recur rest-lines (assoc entries k v))))))))))

(defn- parse-vector
  "Parse vector items from lines at indent level `indent`. Returns [vector remaining-lines].
  Handles enriched '- ' header lines with multiple inline ':key value' pairs
  (e.g. '- :widget :text  :value \"hello\"  :widget-id \"uuid-1\"')."
  [lines indent]
  (loop [lines lines
         items []]
    (if (empty? lines)
      [items lines]
      (let [[line-indent content] (first lines)]
        (cond
          ;; Line at lesser indent or a non-vector-item at our level — done
          (or (< line-indent indent)
              (and (= line-indent indent) (not (vector-item? content))))
          [items lines]

          ;; Line at deeper indent — skip
          (> line-indent indent)
          (recur (rest lines) items)

          ;; A '- ' line at our indent — parse one item
          :else
          (let [item-content (strip-dash content)
                tokens       (tokenize-line item-content)
                n            (count tokens)]
            (cond
              ;; Multiple inline ':key value' pairs (4+ tokens, even count,
              ;; every other token is a keyword) — enriched header line
              (and (>= n 4) (even? n)
                   (every? #(str/starts-with? % ":") (take-nth 2 tokens)))
              (let [inline-entries (parse-inline-entries tokens)
                    [rest-map rest-lines] (parse-map (rest lines) (inc indent))
                    item-map (merge rest-map inline-entries)]
                (recur rest-lines (conj items item-map)))

              ;; '- :key value' — map item with first entry inline
              (and (= n 2) (str/starts-with? (first tokens) ":"))
              (let [first-k (parse-scalar (first tokens))
                    first-v (parse-scalar (second tokens))
                    [rest-map rest-lines] (parse-map (rest lines) (inc indent))
                    item-map (assoc rest-map first-k first-v)]
                (recur rest-lines (conj items item-map)))

              ;; '- :key' — could be a map item with nested value, or a scalar keyword
              (and (= n 1) (str/starts-with? (first tokens) ":"))
              (let [[v rest-lines] (parse-indent-block (rest lines) (inc indent))]
                (if (map? v)
                  (recur rest-lines (conj items (assoc v (parse-scalar (first tokens)) nil)))
                  ;; It was a scalar keyword with no nested content
                  (recur (rest lines) (conj items (parse-scalar (first tokens))))))

              ;; '- scalar' — a scalar item
              :else
              (recur (rest lines) (conj items (parse-scalar item-content))))))))))

(defn- parse-indent-block
  "Parse a value (map or vector) from lines at indent level `indent`.
  Returns [value remaining-lines]."
  [lines indent]
  (if (empty? lines)
    [{} lines]
    (let [[line-indent content] (first lines)]
      (cond
        (< line-indent indent)
        [{} lines]

        (vector-item? content)
        (parse-vector lines line-indent)

        :else
        (parse-map lines line-indent)))))

;; ── Simple EDN pretty-printer ─────────────────────────────────────────

(defn- pretty-edn
  "A minimal EDN pretty-printer: maps and vectors spread one entry/item per
  line with 2-space indentation. Scalars use pr-str. Good enough for the
  widget-tree shape (maps with keyword keys, vectors of maps)."
  [data]
  (letfn [(write [x level]
            (cond
              (map? x)
              (let [entries (seq x)]
                (if (empty? entries)
                  "{}"
                  (str "{\n"
                       (str/join "\n"
                         (for [[k v] entries]
                           (str (indent-spaces (inc level))
                                (pr-str k) " "
                                (write-val v (inc level)))))
                       "\n" (indent-spaces level) "}")))

              (vector? x)
              (if (empty? x)
                "[]"
                (str "[\n"
                     (str/join "\n"
                       (for [item x]
                         (str (indent-spaces (inc level))
                              (write item (inc level)))))
                     "\n" (indent-spaces level) "]"))

              :else (pr-str x)))
          (write-val [v level]
            (if (or (map? v) (vector? v))
              (write v level)
              (pr-str v)))]
    (write data 0)))

(defn indent->edn
  "Convert bracket-stripped (indentation-based) text to pretty-printed EDN text.
  Returns the EDN string, or nil if the indent text doesn't parse."
  [indent-text]
  (if (str/blank? indent-text)
    ""
    (try
      (let [lines (->> (str/split-lines indent-text)
                       (map parse-line)
                       (remove nil?)
                       vec)
            [data _] (parse-indent-block lines 0)]
        (pretty-edn data))
      (catch :default _e
        nil))))

;; ── Locate support ────────────────────────────────────────────────────

(defn- line-indent
  "Indentation level (in 2-space units) of a line, or nil for blank/comment lines."
  [line]
  (when-let [[indent _] (parse-line line)]
    indent))

(defn- starts-vector-item?
  "True if the line (after trimming) starts with '- '."
  [line]
  (let [content (str/triml line)]
    (and (not (str/blank? content))
         (not (str/starts-with? content ";;"))
         (str/starts-with? content "- "))))

(defn indent-node-span
  "[start end) of the widget node containing ':widget-id \"<widget-id>\"' in
  the indent text, or nil when that id is not in the text. A widget node
  starts at either:
  - indent 0 (the root map), or
  - a '- ' line (a vector item that is a map)
  and ends at the line before the next sibling '- ' at the same indentation,
  or before a line at lesser indentation, or EOF."
  [text widget-id]
  (let [needle (str ":widget-id \"" widget-id "\"")
        lines (str/split-lines text)
        needle-line-idx (some (fn [i]
                                 (when (str/includes? (nth lines i) needle)
                                   i))
                               (range (count lines)))]
    (when needle-line-idx
      (let [needle-indent (or (line-indent (nth lines needle-line-idx)) 0)
            ;; Walk backwards to find the start of this node
            start-idx (loop [i (dec needle-line-idx) start needle-line-idx]
                        (if (< i 0)
                          0
                          (let [line (nth lines i)
                                li (line-indent line)]
                            (cond
                              ;; Blank/comment — skip
                              (nil? li) (recur (dec i) start)
                              ;; A '- ' line at indent <= needle-indent — this is a sibling or parent item
                              (and (starts-vector-item? line) (<= li needle-indent))
                              i
                              ;; A line at indent < the vector item's indent — we've gone past the parent
                              (< li needle-indent)
                              (if (starts-vector-item? (nth lines start))
                                start
                                (inc i))
                              :else (recur (dec i) i)))))
            ;; Walk forwards to find the end of this node
            start-indent (or (line-indent (nth lines start-idx)) 0)
            end-idx (loop [i (inc start-idx) end (inc start-idx)]
                      (if (>= i (count lines))
                        (count lines)
                        (let [line (nth lines i)
                              li (line-indent line)]
                          (cond
                            (nil? li) (recur (inc i) end)
                            ;; A '- ' line at the same indent as start — next sibling
                            (and (starts-vector-item? line) (= li start-indent))
                            i
                            ;; A line at indent < start-indent — past the parent
                            (< li start-indent)
                            i
                            :else (recur (inc i) (inc i))))))]
        ;; Convert line indices to character positions
        (let [n-lines (count lines)
              chars-before (fn [idx]
                             (if (>= idx n-lines)
                               (count text)
                               (reduce + 0 (for [i (range idx)]
                                             (inc (count (nth lines i)))))))
              start-pos (chars-before start-idx)
              end-pos (chars-before end-idx)]
          [start-pos end-pos])))))
